/** Size-optimized lodash functions */

/**
 * For usage examples @see https://lodash.com/docs/#pick
 * @returns array of elements picked by the specified indices
 */
export const pick = <T>(arr: { [key: number]: T }, indices: number[]) => indices.map((i) => arr[i])

/**
 * Maps object key names to another ones
 */
export const mapKeys = <K extends string, V, R extends string, O extends Record<K, V>>(
  obj: O,
  map: (key: K, value: V, index: number) => R,
) =>
  Object.fromEntries(
    Object.entries<V>(obj).map(([k, v], index) => [map(k as K, v, index), v]),
  ) as Record<R, V>

/** Maps object values to another ones */
export const mapValues = <K extends string, V, R>(
  obj: Record<K, V>,
  map: (value: V, key: K, index: number) => R,
) =>
  Object.fromEntries(
    Object.entries<V>(obj).map(([k, v], index) => [k, map(v, k as K, index)]),
  ) as Record<K, R>
export const filterObj = <K extends string, V>(
  obj: Record<K, V>,
  pred: (value: V, key: K, index: number) => boolean,
) =>
  Object.fromEntries(
    Object.entries<V>(obj).filter(([k, v], index) => pred(v, k as K, index)),
  ) as Record<K, V>

export const deepMerge = <T extends Record<string, any> = Record<string, any>>(
  target: Record<string, any>,
  ...sources: Record<string, any>[]
): T => {
  if (sources.length === 0) return target as T

  const [source, ...rest] = sources

  for (const key in source)
    if (isObject(source[key])) deepMerge((target[key] ??= {}), source[key])
    else target[key] = source[key]

  return deepMerge(target, ...rest)
}

const isObject = (obj: any): obj is object => obj && typeof obj === "object"

export const isEmpty = (arr: unknown[]) => arr.length === 0
export const median = (arr: number[]) => {
  const sorted = arr.slice().sort((a, b) => a - b)
  const mid = Math.floor(arr.length / 2)

  return arr.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid]
}

export const noop = () => {}
export const throttle = <A extends unknown[], R>(
  fn: (...args: A) => R,
  wait: number,
  opts?: {
    /** Result that will be returned while throttling */
    throttledResult: (...args: A) => R
  },
): ((...args: A) => R) => {
  let lastCall = 0
  let lastResult: R

  return (...args: A) => {
    const now = Date.now()

    if (now - lastCall < wait) return opts?.throttledResult(...args) ?? lastResult

    lastResult = fn(...args)
    lastCall = now

    return lastResult
  }
}

export function compose<A extends unknown[], R>(fn0: (...args: A) => R): (...arg: A) => R
export function compose<A extends unknown[], R, R1>(
  fn1: (arg: R1) => R,
  fn0: (...args: A) => R1,
): (...arg: A) => R
export function compose<A extends unknown[], R, R0, R1>(
  fn2: (arg: R1) => R,
  fn1: (arg: R0) => R1,
  fn0: (...args: A) => R0,
): (...arg: A) => R
export function compose(...fns: Function[]): Function {
  const [first, ...rest] = fns.reverse()

  return (...args: any[]) => rest.reduce((acc, fn) => fn(acc), first(...args))
}

// TODO: Identical implementation of this function is used by companion app. Consider moving it to a shared module.
export const once = <A extends unknown[], R>(fn: (...args: A) => R): ((...args: A) => R) => {
  let called = false
  let result: R

  return (...args: A) => {
    if (called) return result

    called = true
    return (result = fn(...args))
  }
}

export const retryAsync = async <R>(
  fn: () => Promise<R> | R,
  nTimes: number,
  {
    onRetry,
    shouldThrowEarly,
  }: { onRetry?: (error: Error) => void; shouldThrowEarly?: (error: Error) => boolean } = {},
): Promise<R> => {
  try {
    return await fn()
  } catch (e) {
    if (nTimes <= 0 || shouldThrowEarly?.(e as Error)) throw e

    onRetry?.(e as Error)
    return await retryAsync(fn, nTimes - 1)
  }
}
